/* eslint-disable no-unused-vars */
/* eslint-disable no-eval */

import { LICENSE_ASSIGNMENT } from '../../utils/constants';
import { getDate, isAfter90Days } from '../../utils/getDate';
import {
  accountStatus,
  activationDateRanges,
  defaultFilterValue,
  featureUsageModeConst,
  renewalDateRanges,
} from './accountListConfig';

const isHealthScoreMatchFn = (healthScore, selectedFilters) => {
  let isHealthScoreMatch = selectedFilters.healthScoreRange || defaultFilterValue;
  if (selectedFilters.healthScoreRange && selectedFilters.healthScoreRange.length) {
    isHealthScoreMatch = healthScore >= selectedFilters.healthScoreRange[0] && healthScore <= selectedFilters.healthScoreRange[1];
  }
  return isHealthScoreMatch;
};

const isLicenceAssignmentMatchFn = (assignedSeatsPercent, totalSeats, selectedFilters) => {
  const assignmentChecks = {
    [LICENSE_ASSIGNMENT.SITE_WIDE_LICENSE]: () => totalSeats <= -1,
    [LICENSE_ASSIGNMENT.LESS_25]: () => totalSeats > -1 && assignedSeatsPercent >= 0 && assignedSeatsPercent <= 25,
    [LICENSE_ASSIGNMENT.FROM_25_50]: () => assignedSeatsPercent > 25 && assignedSeatsPercent <= 50,
    [LICENSE_ASSIGNMENT.FROM_50_75]: () => assignedSeatsPercent > 50 && assignedSeatsPercent <= 75,
    [LICENSE_ASSIGNMENT.FROM_75_100]: () => assignedSeatsPercent > 75,
  };

  return selectedFilters.licenceAssignment.some((assignment) => assignmentChecks[assignment]?.());
};

const isLicenceStatusMatchFn = (status, renewalDate, selectedFilters) => {
  const currentDate = getDate();
  const isNeverExpire = renewalDate === null;
  const isRenewalDateValid = renewalDate && getDate(renewalDate) >= currentDate.setHours(0, 0, 0, 0);

  const statusChecks = {
    [accountStatus.ACTIVE]: () => status === 'Active' && (isRenewalDateValid || isNeverExpire),
    [accountStatus.TRIAL]: () => status === 'Trial' && (isRenewalDateValid || isNeverExpire),
    [accountStatus.EXPIRED]: () => (status === 'Active' || status === 'Trial') && (!isRenewalDateValid && !isNeverExpire),
    [accountStatus.CHURNED]: () => status === 'Uninstalled',
    [accountStatus.SUSPENDED]: () => status === 'Suspended',
  };

  return selectedFilters.licenceStatus.some((statusKey) => statusChecks[statusKey]?.());
};

const isHealthScoreTrendMatchFn = (trend, isStable, selectedFilters) => {
  const trendChecks = {
    stable: () => trend === 'stable' && isStable,
    rising: () => trend === 'rising',
    falling: () => trend === 'falling',
  };

  return selectedFilters.healthScoreTrend.some((trendKey) => trendChecks[trendKey]?.());
};

const isFeatureUsageAccountsMatchFn = (accountId, featureUsageAccounts, selectedFilters, isNavigatedFromFeatureTaxonomy) => {
  if (!isNavigatedFromFeatureTaxonomy && !selectedFilters.featureUsageFilter.length && selectedFilters.featureUsageMode === featureUsageModeConst.USE) return true;

  const hasFeatureUsageAccountsData = accountId && featureUsageAccounts?.length;
  if (!hasFeatureUsageAccountsData) return false;

  const isUsingFeature = featureUsageAccounts.includes(accountId);
  return selectedFilters.featureUsageMode === featureUsageModeConst.USE
    ? isUsingFeature
    : !isUsingFeature;
};

const isWithinDateRange = (date, dateRange, rangeOptions) => {
  const currentDate = new Date(date);
  currentDate.setHours(0, 0, 0, 0);

  if (dateRange.key) {
    const { startDate, endDate } = rangeOptions[dateRange.key];
    return currentDate >= startDate && currentDate <= endDate;
  }

  if (dateRange.startDate && dateRange.endDate) {
    return currentDate >= new Date(dateRange.startDate) && currentDate <= new Date(dateRange.endDate);
  }

  return true;
};

const hasIntersection = (arr1 = [], arr2 = []) => {
  if (!arr1?.length || !arr2?.length) return false;
  const set = new Set(arr1);
  return arr2.some((id) => set.has(id));
};

const getFilteredAccounts = (props) => {
  const {
    accountList,
    selectedFilters,
    siteConfiguration,
    featureUsageAccounts,
    isNavigatedFromRiskAccount,
    isNavigatedFromFeatureTaxonomy,
  } = props;

  if (!selectedFilters) return accountList;

  const res = accountList?.filter((item) => {
    const {
      healthScore, healthScoreTrend, assignedSeatsPercent, weeklyTrend, installDate,
    } = item;
    const isHealthScoreMatch = isHealthScoreMatchFn(item.healthScore, selectedFilters);
    const isLicenceAssignmentMatch = (!selectedFilters.licenceAssignment.length ? true : isLicenceAssignmentMatchFn(item.assignedSeatsPercent, item.totalSeats, selectedFilters));
    const isLicenceStatusMatch = !selectedFilters.licenceStatus.length || isLicenceStatusMatchFn(item.status, item.renewalDate, selectedFilters);
    const isFeatureUsageAccountsMatch = isFeatureUsageAccountsMatchFn(item.account_id, featureUsageAccounts, selectedFilters, isNavigatedFromFeatureTaxonomy);

    const isStable = selectedFilters.healthScoreTrend.includes('stable')
      ? (!(installDate && isAfter90Days(installDate))
    && !eval(siteConfiguration?.highRiskAccountConfig?.condition ?? 'healthScore <= 30'))
      : true;
    const trend = siteConfiguration?.scoreTrendTimeframe === 'weekly' ? item.weeklyTrend : item.healthScoreTrend;
    const isHealthScoreTrendMatch = !selectedFilters.healthScoreTrend.length || isHealthScoreTrendMatchFn(trend, isStable, selectedFilters);

    const isPackageMatch = !selectedFilters.package.length || hasIntersection(selectedFilters.package, item.packages);
    const isPackageVersionMatch = !selectedFilters.packageVersion.length || hasIntersection(selectedFilters.packageVersion, item.packageVersions);
    const isSalesforceInstanceMatch = !selectedFilters.salesforceInstance.length || selectedFilters.salesforceInstance.includes(item.salesforceInstance);
    // const isAccountVisibilityMatch = selectedFilters.accountVisibility === defaultFilterValue || item.visibility === selectedFilters.accountVisibility;
    const isCountryMatch = !selectedFilters.country.length || selectedFilters.country.includes(item.countryCode);
    const isLanguageMatch = !selectedFilters.language.length || selectedFilters.language.includes(item.languageLocale);
    const isOrganisationEditionMatch = !selectedFilters.organisationEdition.length || selectedFilters.organisationEdition.includes(item.orgEdition);
    const isAssignedSeatsPercent = (selectedFilters.range[0] >= 0 && selectedFilters.range[0] <= 100) ? (item.seatsAssigned >= selectedFilters.range[0] && item.seatsAssigned <= (selectedFilters.range[1] >= 100 ? 100000 : selectedFilters.range[1])) : (item.seatsAssigned > 100);
    const isOldAccount = isAfter90Days(item.installDate);

    const isActivationDateRangeMatch = isWithinDateRange(
      item.activationDate,
      selectedFilters.activationDate,
      activationDateRanges,
    );
    const isRenewalDateRangeMatch = isWithinDateRange(
      item.renewalDate,
      selectedFilters.renewalDate,
      renewalDateRanges,
    );
    const isSearchMatch = selectedFilters.searchQuery === ''
      || (item.accountName && item.accountName.toLowerCase()
        .includes(selectedFilters.searchQuery.toLowerCase()))
      || (item.account_id && item.account_id.includes(selectedFilters.searchQuery));

    return (
      isLicenceAssignmentMatch
      && isLicenceStatusMatch
      && isPackageMatch
      && isPackageVersionMatch
      && isSalesforceInstanceMatch
      // && isAccountVisibilityMatch
      && isCountryMatch
      && isLanguageMatch
      && isOrganisationEditionMatch
      && isAssignedSeatsPercent
      && isActivationDateRangeMatch
      && isSearchMatch
      && isRenewalDateRangeMatch
      && isFeatureUsageAccountsMatch
      && isHealthScoreMatch
      && isHealthScoreTrendMatch
      && (isNavigatedFromRiskAccount ? (isOldAccount) : true)
      && eval(selectedFilters?.condition ?? true)
    );
  });
  return res;
};

export { getFilteredAccounts };
